import type { StudyNote } from '@/pages/learn/study/studyNotesApi';
import type { StudyMindMap, StudyMindMapNode } from '@/pages/learn/study/studyMindMapApi';
import type { StudyAnalysis } from '@/pages/learn/study/studyAnalysisApi';
import type {
  StudyAnalysisReviewItem,
  StudyPracticeSet,
  StudyAnswerCheck,
} from '@/pages/learn/study/studyAnalysisFollowUpApi';
import { demoDocuments } from './exampleData';

export const DEMO_WORKBOOK_ID = demoDocuments()[0].id;
export const DEMO_ANALYSIS_ID = 'demo-maths-reading';
export const DEMO_SET_ID = 'demo-maths-practice';
export const DEMO_MAP_ID = 'demo-maths-connections';
export const DEMO_PRACTICE_PATH = `/learn/study/materials/${DEMO_WORKBOOK_ID}/analysis?analysis=${DEMO_ANALYSIS_ID}&practiceSet=${DEMO_SET_ID}`;
export const DEMO_MAP_PATH = `/learn/study/subjects/mathematics/mind-maps/${DEMO_MAP_ID}`;
const stamp = '2026-10-17T04:00:00.000Z';
export const DEMO_LEARNING_NOTICE =
  'Prepared fictional learning outcomes · no live AI or Stars. Edits and practice stay in this demo tab. Results do not measure your ability.';

export function demoLearningNotes(): StudyNote[] {
  const bodies = [
    [
      'example-notes',
      'Fractions — class notes',
      'Fractions',
      `Prepared fictional class notes — Alex, Year 10, Term 4.

QUESTION AND RESULT
Add 1/3 and 1/9. The exact result is 4/9, or 0.(4), or 44.(4)%. Parentheses mark repeating digits.

WHY THE METHOD WORKS
A denominator names the size of each equal part. Thirds and ninths have different sizes, so first express both fractions in ninths. Splitting each third into three gives 1/3 = 3/9. Then 3/9 + 1/9 = 4/9. Add the numerators; keep the shared denominator. Adding both denominators would give 2/12, which is not equivalent.

CHECK AND PRECISION
4/9 is between 1/3 and 1/2. To convert to a percentage, multiply by 100: 400/9% = 44.(4)%. Writing 44% rounds the value; it is not an exact equality. Keep the fraction in intermediate steps.

CONNECT TO RATIO
Share 40 units in the ratio 2:3. There are 2 + 3 = 5 equal parts. Each part is 40/5 = 8. The shares are 16 and 24. Check 16 + 24 = 40 and 16:24 = 2:3. The first share is 2/5 of the whole; 2/3 compares the first share with the second.

PREPARED REFLECTION
I first used 2/3 of the total. Counting all five parts corrects that mistake. Reversing to 3:2 swaps the shares to 24 and 16 without changing the total.

RETRIEVAL CHECK
Without looking: explain why a common denominator is needed, then split 60 in a 2:3 ratio. Check: 24 and 36; total 60 and ratio 2:3.

SOURCE
Alex's fictional mathematics workbook; see the saved fraction and ratio working and the prepared practice set.`,
    ],
    [
      'example-notes-geometry',
      'Geometry - height, slant and units',
      'Geometry',
      `Prepared fictional class notes — Alex, Year 10, Term 4.

CONE QUESTION AND RESULTS
Radius r = 3 cm; perpendicular height h = 4 cm. Slant length l = 5 cm. Volume V = 12π ≈ 37.6991 cm³. Curved area = 15π ≈ 47.1239 cm². Total surface area = 24π ≈ 75.3982 cm².

DERIVATION
The radius and perpendicular height form the legs of a right triangle. Its hypotenuse is l = √(3² + 4²) = 5 cm. The base area is πr² = 9π cm². A cone has one third of the matching cylinder's volume: V = (1/3)(9π)(4) = 12π cm³. The curved surface unfolds into a sector: area = (1/2) × l × circumference = (1/2) × 5 × 6π = 15π cm². Add one base to obtain 24π cm².

COMMON MISTAKE
Volume uses perpendicular height h. Curved area uses slant length l. Substituting h into πrl would omit part of the surface.

SPHERE CONNECTION
At radius 2 cm, surface area = 4π(2²) = 16π ≈ 50.2655 cm². Volume = (4/3)π(2³) = 32π/3 ≈ 33.5103 cm³. Doubling radius to 4 cm multiplies area by 2² = 4 and volume by 2³ = 8.

CHANGED CONDITION
Keep cone radius 3 cm and change height to 12 cm. Then l = √153 = 3√17 ≈ 12.3693 cm. V = 36π ≈ 113.097 cm³. S = 9π + 9π√17 ≈ 144.852 cm². Volume triples; surface area does not, because its base stays unchanged and the slant changes nonlinearly.

RETRIEVAL CHECK
Name the correct height in each formula before calculating. Check length in cm, area in cm² and volume in cm³. Read the matching saved cone and sphere working for each calculation.`,
    ],
    [
      'example-notes-algebra',
      'Algebra - inverse operations and exact checks',
      'Algebra',
      `Prepared fictional class notes — Alex, Year 10, Term 4.

LINEAR QUESTION AND RESULT
Solve 3x + 2 = 14. Subtract 2 from both sides: 3x = 12. Divide both sides by 3: x = 4. Check in the original equation: 3(4) + 2 = 14.

WHY IT WORKS
Applying the same operation to both sides preserves equality. Undo the final addition before the earlier multiplication. The graph y = 3x + 2 intersects y = 14 at (4, 14).

EXACT VALUE CHECK
If the right side becomes 3, subtraction gives 3x = 1 and x = 1/3 = 0.(3). Exact substitution gives 3(1/3) + 2 = 3. Using the rounded 0.33 gives 2.99, so use ≈ for that decimal.

QUADRATIC CONNECTION
For x² − 5x + 6 = 0, a = 1, b = −5, c = 6. D = b² − 4ac = 25 − 24 = 1. The quadratic formula gives x = (5 ± 1)/2, hence roots 2 and 3. Check: 4 − 10 + 6 = 0 and 9 − 15 + 6 = 0. The graph meets the x-axis at (2,0) and (3,0).

SOLVING VERSUS EVALUATING
Solving starts from an output and finds an input. Evaluating f(x) = 3x + 2 at input 2 gives f(2) = 8; this is not the solution of 3x + 2 = 14.

PREPARED REVIEW REFLECTION
A checked answer is evidence for this calculation only. To test transfer, solve 5x − 7 = 18 without these steps: add 7 to obtain 5x = 25, divide by 5 to get x = 5, then check 25 − 7 = 18. No mastery claim follows from this prepared example.`,
    ],
    [
      'example-notes-review',
      'Review record - fixing a ratio mistake',
      'Review',
      `Prepared fictional review record — not your performance.

ORIGINAL QUESTION
Share 40 units in the ratio 2:3. The prepared first answer was 26⅔ and 13⅓, based on taking 2/3 of the whole. That preserves the total but gives a 2:1 ratio, so it fails the ratio condition.

CORRECTION AND REASON
The whole has five equal parts, not three. Each part is 8. Two parts give 16; three parts give 24. Both checks now hold: 16 + 24 = 40 and 16:24 = 2:3.

LATER RETRIEVAL EXAMPLE
For 60 units in the ratio 2:3: five parts; each part 12; shares 24 and 36. Check 24 + 36 = 60 and 24:36 = 2:3. The first share remains 2/5 of the whole.

NEXT REVIEW
Reopen the ratio practice question from Review and select an answer before looking at the explanation. Explain the difference between part-to-part and part-to-whole. Completing a review records an action; it does not establish mastery.

LINKED OUTCOMES
The prepared practice set records one ratio error among five attempted questions. The review queue points back to that same ratio question. The fractions notebook and concept map explain the correction.`,
    ],
  ];
  return bodies.map(([id, title, topic, body]) => ({
    id,
    title,
    topic,
    body,
    subject_key: 'mathematics',
    term_id: 'example-term',
    class_date: '2026-10-17',
    pages: [],
    version: 1,
    status: 'active',
    available: true,
    updated_at: stamp,
  }));
}

export function demoLearningMap(): StudyMindMap {
  const specs: Array<[string, string, string, StudyMindMapNode['color']]> = [
    [
      'centre',
      'Exact values, diagrams and checks',
      'Prepared fictional concept map. Read the worked examples, then use Text view or Details to edit.',
      'sky',
    ],
    [
      'fraction',
      'Fractions need equal-sized parts',
      '1/3 = 3/9, so 1/3 + 1/9 = 4/9. Keep the common denominator 9.',
      'coral',
    ],
    [
      'percent',
      '4/9 = 44.(4)%',
      'Multiply by 100. 44% is rounded; do not replace an exact fraction during working.',
      'sunshine',
    ],
    [
      'ratio',
      '2:3 of 40 gives 16 and 24',
      'Five parts; 40/5 = 8. Check total 40 and ratio 2:3. The first share is 2/5 of the whole.',
      'mint',
    ],
    [
      'cone',
      'Cone: h and l have different jobs',
      'r=3, h=4. l=√(9+16)=5. V=(1/3)πr²h=12π. Curved area πrl=15π; total 24π.',
      'sky',
    ],
    [
      'sphere',
      'Sphere: radius controls area and volume',
      'r=2: area 16π cm², volume 32π/3 cm³. Double r: area ×4, volume ×8.',
      'bubblegum',
    ],
    [
      'units',
      'Check the units',
      'Length cm; area cm²; volume cm³. Name the quantity before choosing the formula.',
      'sunshine',
    ],
    [
      'linear',
      '3x + 2 = 14 gives x = 4',
      'Subtract 2 from both sides, then divide by 3. Check 3(4)+2=14.',
      'coral',
    ],
    [
      'exact',
      'Changed right side: x = 1/3',
      '3x+2=3 gives x=1/3. Using 0.33 gives 2.99; the rounded value is only approximate.',
      'mint',
    ],
    [
      'quadratic',
      'x² − 5x + 6 = 0: roots 2 and 3',
      'D=25−24=1. x=(5±1)/2. Substitute both roots into the original equation.',
      'sky',
    ],
  ];
  const nodes: StudyMindMapNode[] = specs.map(([id, text, note, color], i) => ({
    id,
    text,
    note,
    color,
    kind: i === 0 ? 'topic' : 'idea',
    x: i === 0 ? 430 : ((i - 1) % 3) * 430,
    y: i === 0 ? 0 : 170 + Math.floor((i - 1) / 3) * 190,
    origin: 'ai',
  }));
  const pairs = [
    ['centre', 'fraction', 'number'],
    ['fraction', 'percent', 'convert exactly'],
    ['fraction', 'ratio', 'equal parts'],
    ['centre', 'cone', 'geometry'],
    ['cone', 'sphere', 'compare solids'],
    ['cone', 'units', 'check dimensions'],
    ['centre', 'linear', 'algebra'],
    ['linear', 'exact', 'change input'],
    ['linear', 'quadratic', 'check by substitution'],
  ];
  return {
    id: DEMO_MAP_ID,
    title: 'Alex’s maths connections — prepared demo',
    subject_key: 'mathematics',
    term_id: 'example-term',
    version: 1,
    node_count: nodes.length,
    edge_count: pairs.length,
    source_document_ids: [DEMO_WORKBOOK_ID],
    content: {
      nodes,
      edges: pairs.map(([from, to, label], i) => ({
        id: `link-${i}`,
        from,
        to,
        label,
        arrow: 'end',
      })),
      viewport: { x: 0, y: 0, zoom: 1 },
    },
    limits: { nodes_per_map: 300, edges_per_map: 600 },
    created_at: stamp,
    updated_at: stamp,
  };
}

export function demoPracticeSet(): StudyPracticeSet {
  const rows = [
    [
      'What is 1/3 + 1/9?',
      ['2/12', '2/9', '4/9', '4/12'],
      'C',
      '4/9',
      'Change 1/3 to 3/9. Add 3/9 + 1/9 = 4/9. Keep equal-sized ninths.',
      'C',
    ],
    [
      'Share 40 units in the ratio 2:3. Which pair is correct?',
      ['26⅔ and 13⅓', '16 and 24', '20 and 20', '24 and 16'],
      'B',
      '16 and 24',
      'There are 2+3=5 parts. Each is 40/5=8. Shares: 2×8=16 and 3×8=24. Check sum 40 and ratio 2:3. 2/3 compares the shares; 2/5 is the first share of the whole.',
      'A',
    ],
    [
      'A cone has r = 3 cm and perpendicular h = 4 cm. What is its volume?',
      ['12π cm³', '15π cm³', '24π cm³', '36π cm³'],
      'A',
      '12π cm³',
      'V=(1/3)πr²h=(1/3)π×9×4=12π cm³. The slant is 5 cm, but volume uses perpendicular h=4.',
      'A',
    ],
    [
      'Solve 3x + 2 = 3 exactly.',
      ['0.33 exactly', '3', '1/2', '1/3'],
      'D',
      '1/3',
      'Subtract 2: 3x=1. Divide by 3: x=1/3. Check 3×(1/3)+2=3. The rounded 0.33 gives 2.99.',
      'D',
    ],
    [
      'A sphere’s radius doubles. What happens to its surface area and volume?',
      ['Both double', 'Area ×8; volume ×4', 'Area ×4; volume ×8', 'Both ×4'],
      'C',
      'Area ×4; volume ×8',
      'Area 4πr² scales by 2²=4. Volume (4/3)πr³ scales by 2³=8. Different dimensions give different scaling.',
      'C',
    ],
  ] as const;
  return {
    id: DEMO_SET_ID,
    analysis_id: DEMO_ANALYSIS_ID,
    document_id: DEMO_WORKBOOK_ID,
    status: 'ready',
    question_kinds: ['multiple_choice'],
    stars_charged: 0,
    stars_refunded: 0,
    refunded: false,
    check_stars_per_attempt: 0,
    error_code: null,
    created_at: stamp,
    completed_at: stamp,
    questions: rows.map(([stem, options, correct, answer, worked, submitted], i) => ({
      id: `pq${i + 1}`,
      kind: 'multiple_choice',
      stem,
      options: [...options],
      attempts: 1,
      answer_available: true,
      answer: { answer, correct_option: correct, worked_solution: worked },
      latest_attempt: {
        id: `prepared-attempt-${i}`,
        question_id: `pq${i + 1}`,
        practice_set_id: DEMO_SET_ID,
        status: 'checked',
        verdict: correct === submitted ? 'correct' : 'incorrect',
        hint:
          correct === submitted
            ? 'Prepared example: the selected answer matches the exact calculation.'
            : 'Prepared example: count every equal part in the whole before sharing.',
        student_answer_text: submitted,
        stars: 0,
        stars_refunded: 0,
        refunded: false,
        error_code: null,
        created_at: stamp,
        completed_at: stamp,
      } satisfies StudyAnswerCheck,
    })),
  };
}
export function demoLearningAnalysis(): StudyAnalysis {
  return {
    id: DEMO_ANALYSIS_ID,
    status: 'succeeded',
    version: 1,
    parent_analysis_id: null,
    primary_document_id: DEMO_WORKBOOK_ID,
    document_ids: [DEMO_WORKBOOK_ID],
    prompt_version: 'prepared-demo',
    model_ids: [],
    progress: {
      pages_total: 1,
      pages_read: 1,
      pages_failed: 0,
      questions_total: 0,
      questions_done: 0,
    },
    stars: { per_page: 0, quoted: 0, charged: 0, refunded: 0 },
    refund: { state: 'none', owed_stars: 0 },
    error_code: null,
    created_at: stamp,
    completed_at: stamp,
    pages: [
      {
        page_index: 0,
        document_id: DEMO_WORKBOOK_ID,
        page_number: 1,
        status: 'read',
        error_code: null,
        legible: 'good',
        handwriting: 'none',
        blocks: [
          {
            id: 'prepared-overview',
            kind: 'text',
            text: 'Prepared overview of Alex’s original mathematics examples. This is not a live page analysis. The full four-page workbook remains available in Materials.',
            region: null,
            confidence: 'high',
            provenance: 'learner_edit',
          },
        ],
      },
    ],
    result: {
      material_type: 'other',
      subject: { key: 'mathematics', label: 'Mathematics', confidence: 'high' },
      topic: {
        label: 'Exact values, solid geometry and checking equations — prepared demo',
        confidence: 'high',
      },
      year_level_guess: { value: 10, confidence: 'high' },
      questions: [],
      key_points: [
        {
          text: 'Use equal-sized parts: 1/3 + 1/9 = 4/9. Keep the exact fraction until final rounding.',
          source: 'ai_explanation',
          block_ids: [],
        },
        {
          text: 'A 2:3 share has five parts: 40 gives 16 and 24. Check both total and ratio.',
          source: 'ai_explanation',
          block_ids: [],
        },
        {
          text: 'Cone volume uses perpendicular h; curved area uses slant l. For r=3,h=4: l=5,V=12π,S=24π.',
          source: 'ai_explanation',
          block_ids: [],
        },
        {
          text: 'Substitute answers into the original equation. 3x+2=3 needs exact x=1/3; 0.33 gives 2.99.',
          source: 'ai_explanation',
          block_ids: [],
        },
        {
          text: 'Sphere area scales with radius squared; volume scales with radius cubed.',
          source: 'ai_explanation',
          block_ids: [],
        },
      ],
      key_terms: [
        {
          term: 'Exact value',
          meaning: 'A value kept as a fraction or symbolic expression without rounding.',
          source: 'ai_explanation',
        },
        {
          term: 'Part-to-whole',
          meaning: 'A share compared with the total of all shares.',
          source: 'ai_explanation',
        },
      ],
      formulas: [
        { latex: 'V=\\frac13\\pi r^2h', label: 'Cone volume', block_ids: [] },
        { latex: 'S=4\\pi r^2', label: 'Sphere surface area', block_ids: [] },
      ],
      dates: [],
      warnings: [],
      rubric: null,
    },
    learner_confirmed: { subject: true, topic: true, material_type: true },
    edits: [],
    learner_seen_at: stamp,
  };
}
export function demoLearningReviews(): StudyAnalysisReviewItem[] {
  return [
    {
      kind: 'analysis',
      id: 'demo-ratio-review',
      analysis_id: DEMO_ANALYSIS_ID,
      document_id: DEMO_WORKBOOK_ID,
      document_name: 'Alex’s prepared mathematics practice',
      question_id: 'pq2',
      question_number: '2',
      practice_set_id: DEMO_SET_ID,
      topic: 'Ratio: part-to-part and part-to-whole',
      subject: { subject_key: 'mathematics', display_name: 'Mathematics' },
      source: 'practice_wrong',
      created_at: stamp,
      review_due_at: stamp,
      due: true,
    },
  ];
}
